/**
 * Beautiful Custom Select Component
 * 
 * A modern, animated dropdown select with search, icons, and keyboard navigation.
 * Reusable across all applications.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Search, X, AlertCircle } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface SelectOptionBadge {
  text: string;
  tone?: 'success' | 'warning' | 'neutral';
}

export interface SelectOption {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  badge?: SelectOptionBadge;
  disabled?: boolean;
}

export interface SelectNewProps {
  /** Label text */
  label?: string;
  /** Options list */
  options: SelectOption[];
  /** Selected value */
  value?: string | number | null;
  /** Change handler */
  onChange?: (value: string | number) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Required field */
  required?: boolean;
  /** Enable search/filter */
  searchable?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Allow clearing selection */
  clearable?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Color theme */
  theme?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  /** Start icon */
  startIcon?: React.ReactNode;
  /** Full width */
  fullWidth?: boolean;
  /** Custom class name */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Max height for dropdown */
  maxHeight?: number;
  /** Render dropdown above clipping containers such as modals */
  menuPortal?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const sizeStyles = {
  sm: {
    trigger: 'h-9 px-3 text-sm',
    option: 'px-3 py-2 text-sm',
    icon: 16,
  },
  md: {
    trigger: 'h-11 px-4 text-sm',
    option: 'px-4 py-2.5 text-sm',
    icon: 18,
  },
  lg: {
    trigger: 'h-14 px-5 text-base',
    option: 'px-5 py-3 text-base',
    icon: 20,
  },
};

const badgeToneStyles = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200/70',
  warning: 'bg-amber-100 text-amber-700 border-amber-200/70',
  neutral: 'bg-gray-100 text-gray-600 border-gray-200/70',
};

const optionToneStyles = {
  success: {
    base: 'bg-emerald-50/90 hover:bg-emerald-100/80',
    active: 'bg-emerald-100',
  },
  warning: {
    base: 'bg-amber-50/90 hover:bg-amber-100/80',
    active: 'bg-amber-100',
  },
  neutral: {
    base: 'bg-gray-50/90 hover:bg-gray-100/80',
    active: 'bg-gray-100',
  },
};

const themeStyles = {
  default: {
    ring: 'focus-within:ring-blue-500/20 focus-within:border-blue-500',
    check: 'text-blue-600',
    accent: 'bg-blue-50',
    searchFocus: 'focus:ring-blue-500/20 focus:border-blue-500',
  },
  primary: {
    ring: 'focus-within:ring-purple-500/20 focus-within:border-purple-500',
    check: 'text-purple-600',
    accent: 'bg-purple-50',
    searchFocus: 'focus:ring-purple-500/20 focus:border-purple-500',
  },
  success: {
    ring: 'focus-within:ring-emerald-500/20 focus-within:border-emerald-500',
    check: 'text-emerald-600',
    accent: 'bg-emerald-50',
    searchFocus: 'focus:ring-emerald-500/20 focus:border-emerald-500',
  },
  warning: {
    ring: 'focus-within:ring-amber-500/20 focus-within:border-amber-500',
    check: 'text-amber-600',
    accent: 'bg-amber-50',
    searchFocus: 'focus:ring-amber-500/20 focus:border-amber-500',
  },
  danger: {
    ring: 'focus-within:ring-red-500/20 focus-within:border-red-500',
    check: 'text-[#c00035]',
    accent: 'bg-red-50',
    searchFocus: 'focus:ring-red-500/20 focus:border-red-500',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export const SelectNew: React.FC<SelectNewProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  error,
  helperText,
  disabled = false,
  required = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  clearable = false,
  size = 'md',
  theme = 'default',
  startIcon,
  fullWidth = true,
  className = '',
  emptyMessage = 'No options found',
  maxHeight = 280,
  menuPortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [portalPosition, setPortalPosition] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    width: number;
    listMaxHeight: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Get selected option
  const selectedOption = useMemo(() =>
    options.find(opt => opt.value === value),
    [options, value]
  );

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(opt =>
      opt.label.toLowerCase().includes(query) ||
      opt.description?.toLowerCase().includes(query) ||
      opt.badge?.text.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !menuPortal) {
      setPortalPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportPadding = 8;
      const gap = 6;
      const searchHeight = searchable ? 58 : 0;
      const optionHeight = size === 'lg' ? 52 : size === 'sm' ? 36 : 42;
      // Size to the real option count so short lists never trigger a flip.
      const contentHeight = options.length
        ? options.length * optionHeight + 8
        : 130;
      const desiredHeight = Math.min(maxHeight, contentHeight) + searchHeight;
      const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
      const spaceAbove = rect.top - gap - viewportPadding;
      // Stay below the trigger unless that space is genuinely unusable.
      const openAbove =
        spaceBelow < Math.min(desiredHeight, 180) && spaceAbove > spaceBelow;
      const availableHeight = openAbove ? spaceAbove : spaceBelow;

      setPortalPosition({
        left: Math.max(
          viewportPadding,
          Math.min(rect.left, window.innerWidth - rect.width - viewportPadding),
        ),
        ...(openAbove
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
        width: Math.min(rect.width, window.innerWidth - viewportPadding * 2),
        listMaxHeight: Math.max(
          96,
          Math.min(maxHeight, availableHeight - searchHeight),
        ),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, maxHeight, menuPortal, options.length, searchable, size]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  }, [isOpen, searchable]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlighted = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          const opt = filteredOptions[highlightedIndex];
          if (!opt.disabled) {
            onChange?.(opt.value);
            setIsOpen(false);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  }, [disabled, isOpen, highlightedIndex, filteredOptions, onChange]);

  // Handle option selection
  const handleSelect = (opt: SelectOption) => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.('');
    setSearchQuery('');
  };

  const sizeConfig = sizeStyles[size];
  const themeConfig = themeStyles[theme];
  const dropdown = (
    <AnimatePresence>
      {isOpen && (!menuPortal || portalPosition) ? (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className={`z-50 bg-white border border-gray-200 rounded-xl shadow-lg shadow-gray-200/50 overflow-hidden ${
            menuPortal ? 'fixed' : 'absolute w-full mt-1.5'
          }`}
          style={
            menuPortal && portalPosition
              ? {
                  left: portalPosition.left,
                  top: portalPosition.top,
                  bottom: portalPosition.bottom,
                  width: portalPosition.width,
                  zIndex: 10000,
                }
              : undefined
          }
        >
          {/* Search Input */}
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className={`w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 ${themeConfig.searchFocus} transition-all`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 text-gray-400"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Options List */}
          <div
            ref={listRef}
            className="overflow-y-auto py-1"
            style={{
              maxHeight: portalPosition?.listMaxHeight ?? maxHeight,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                  <Search size={20} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">{emptyMessage}</p>
              </div>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = opt.value === value;
                const isHighlighted = index === highlightedIndex;
                const badgeTone = opt.badge?.tone ?? 'neutral';
                const toneStyles = opt.badge ? optionToneStyles[badgeTone] : null;

                return (
                  <motion.button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    disabled={opt.disabled}
                    className={`
                      w-full flex items-center gap-3 ${sizeConfig.option}
                      text-left transition-all duration-150
                      ${opt.disabled
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : isHighlighted || isSelected
                          ? toneStyles?.active ?? themeConfig.accent
                          : toneStyles?.base ?? 'hover:bg-gray-50'
                      }
                      ${isSelected ? 'font-medium' : ''}
                    `}
                  >
                    {/* Option Icon */}
                    {opt.icon && (
                      <span className="flex-shrink-0 text-gray-500">
                        {opt.icon}
                      </span>
                    )}

                    {/* Option Content */}
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-gray-900 capitalize">
                        {opt.label}
                      </div>
                      {opt.description && (
                        <div className="truncate text-xs text-gray-500 mt-0.5">
                          {opt.description}
                        </div>
                      )}
                    </div>

                    {opt.badge && (
                      <span
                        className={`
                          flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full
                          text-[10px] font-semibold uppercase tracking-wide border
                          ${badgeToneStyles[badgeTone]}
                        `}
                      >
                        {opt.badge.text}
                      </span>
                    )}

                    {/* Check Mark */}
                    {isSelected && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`flex-shrink-0 ${themeConfig.check}`}
                      >
                        <Check size={sizeConfig.icon} strokeWidth={2.5} />
                      </motion.span>
                    )}
                  </motion.button>
                );
              })
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <div
      ref={containerRef}
      className={`relative ${fullWidth ? 'w-full' : 'w-auto'} ${className}`}
    >
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-[#c00035] ml-1">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          relative w-full flex items-center justify-between
          ${sizeConfig.trigger}
          bg-white border rounded-xl
          ${error
            ? 'border-red-300 focus-within:ring-red-500/20 focus-within:border-red-500'
            : `border-gray-200 ${themeConfig.ring}`
          }
          ${disabled
            ? 'bg-gray-50 cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:border-gray-300'
          }
          focus:outline-none focus-within:ring-2
          transition-all duration-200
          group
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Start Icon */}
          {startIcon && (
            <span className="text-gray-400 flex-shrink-0">
              {startIcon}
            </span>
          )}

          {/* Selected Value or Placeholder */}
          {selectedOption ? (
            <div className="flex items-center gap-2 min-w-0">
              {selectedOption.icon && (
                <span className="flex-shrink-0">{selectedOption.icon}</span>
              )}
              <span className="truncate text-gray-900 font-medium capitalize">
                {selectedOption.label}
              </span>
            </div>
          ) : (
            <span className="text-gray-400 truncate">{placeholder}</span>
          )}
        </div>

        {/* Action Icons */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {/* Clear Button */}
          {clearable && selectedOption && !disabled && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={sizeConfig.icon - 2} />
            </motion.button>
          )}

          {/* Chevron */}
          <motion.span
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-gray-400 group-hover:text-gray-600"
          >
            <ChevronDown size={sizeConfig.icon} />
          </motion.span>
        </div>
      </button>

      {/* Dropdown */}
      {menuPortal && typeof document !== 'undefined'
        ? createPortal(dropdown, document.body)
        : dropdown}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-1.5 mt-1.5 text-sm text-[#c00035]"
        >
          <AlertCircle size={14} />
          <span>{error}</span>
        </motion.div>
      )}

      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default SelectNew;

